import { useMemo } from 'react';

import type { Button4 as Button } from '~/amplienceTypes/schemas/exported/button-schema';
import type { CategoryList } from '~/amplienceTypes/schemas/imported/category-recommendations-schema';
import type { Breadcrumb } from '~/utilities/graphql/codegen';

const flattenPath = (path: string) => path?.toLowerCase().replace(/\s/g, '_');
const CURRENT_CATEGORY_BREADCRUMB_INDEX = 1;

type Props = {
  staticCategoryPath?: string;
  breadcrumbs?: Breadcrumb[];
  categoryList?: CategoryList;
};

export const useRecommendationsCategories = ({
  staticCategoryPath,
  breadcrumbs,
  categoryList,
}: Props): Button[] =>
  useMemo(() => {
    const buttons: Button[] = [];

    if (staticCategoryPath && categoryList?.length) {
      const path = flattenPath(staticCategoryPath);

      // Find specific category recommendations
      const category = categoryList.find(
        ({ staticCategoryPath }) => flattenPath(staticCategoryPath as string) === path
      );

      if (category) {
        category?.categoryTags?.forEach(({ button }) => {
          const categoryTag = button as Button;

          if (categoryTag.url && categoryTag.text) {
            buttons.push(categoryTag);
          }
        });
      } else {
        // Move to fallback recommendations
        const root = path.split('/')?.[0];
        const rootCategory = categoryList.find(
          ({ staticCategoryPath }) => flattenPath(staticCategoryPath as string) === root
        );

        const localisedCategoryName = breadcrumbs?.[CURRENT_CATEGORY_BREADCRUMB_INDEX]?.name;
        const localisedPath = flattenPath(
          breadcrumbs?.[CURRENT_CATEGORY_BREADCRUMB_INDEX]?.url as string
        );

        const isDuplicate = (url: string) =>
          localisedPath?.includes(flattenPath(url)) || flattenPath(url).includes(path);

        rootCategory?.categoryTags?.forEach(({ button }) => {
          const categoryTag = button as Button;

          if (categoryTag.text && categoryTag.url && !isDuplicate(categoryTag.url)) {
            buttons.push(categoryTag);
          }
        });

        if (buttons.length && localisedPath && localisedCategoryName) {
          buttons.unshift({ text: localisedCategoryName, url: localisedPath });
        }
      }
    }

    return buttons;
  }, [staticCategoryPath, breadcrumbs, categoryList]);
